import apiService from "../services/apiService";
import { CustomerAddress } from "../types/supplier";

export const CUSTOMER_ADDRESS_API = "/customers/get-customer-address";
export const CUSTOMER_ADDRESS_SAVE_API = "/customers/add-update-address";

export const ADDRESS_TYPE_LABELS: Record<string, string> = {
  registered_address: "Registered Address",
  branch_address: "Branch Address",
  warehouse_address: "Warehouse Address",
  business_address: "Business Address",
};

const ADDRESS_TYPES = new Set(Object.keys(ADDRESS_TYPE_LABELS));

export function getAddressTypeLabel(type?: string | null): string {
  if (!type) return "Address";
  return ADDRESS_TYPE_LABELS[type] || type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function pickList(response: any): any[] {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.address)) return response.data.address;
  if (Array.isArray(response?.data?.addresses)) return response.data.addresses;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.addresses)) return response.addresses;
  if (Array.isArray(response?.address)) return response.address;
  return [];
}

export function normalizeCustomerAddress(raw: any): CustomerAddress {
  const typeRaw = String(
    raw?.address_type ?? raw?.addressType ?? raw?.type ?? raw?.status ?? ""
  ).trim();
  const address_type = typeRaw as CustomerAddress["address_type"];

  const address_line1 = String(raw?.address_line1 ?? raw?.addressLine1 ?? "").trim();
  const address_line2 = String(raw?.address_line2 ?? raw?.addressLine2 ?? "").trim();
  const city = String(raw?.city ?? "").trim();
  const state = String(raw?.state_id ?? raw?.stateId ?? raw?.state ?? "").trim();
  const pincode = String(raw?.pincode ?? raw?.pin_code ?? raw?.pinCode ?? "").trim();
  const gstin = String(raw?.gstin ?? raw?.gstin_number ?? raw?.gstNo ?? "").trim();

  const formatted =
    raw?.formattedAddress ||
    [address_line1, address_line2, city, state, pincode].filter(Boolean).join(", ");

  return {
    id: String(raw?.id ?? ""),
    address_line1,
    address_line2,
    city,
    state,
    pincode,
    gstin,
    formattedAddress: formatted,
    address_type: ADDRESS_TYPES.has(address_type) ? address_type : ("" as CustomerAddress["address_type"]),
  };
}

export async function fetchCustomerAddresses(customerId: string): Promise<CustomerAddress[]> {
  if (!customerId) return [];
  const response = await apiService.get<any>(`${CUSTOMER_ADDRESS_API}/${customerId}`);
  return pickList(response).map(normalizeCustomerAddress);
}

/** Shape used by schedule approve / bill-to dropdowns. */
export function toBillToAddress(addr: CustomerAddress) {
  const parts = [
    addr.address_line1,
    addr.address_line2,
    addr.city,
    addr.state,
    addr.pincode,
  ].filter(Boolean);
  return {
    id: String(addr.id),
    label: getAddressTypeLabel(addr.address_type),
    address: addr.formattedAddress || parts.join(", "),
    gstin: addr.gstin || undefined,
    stateId: addr.state || undefined,
  };
}
